import React, { useEffect, useRef, useState } from 'react';
import { suppliesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Camera, Loader2, Palette, Trash2, ImageIcon, Eye } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PackingSlipTemplateDialog — KR-only editor for the branding on the
 * Packing Slip PDF: uploaded logo (auto-cropped to square, up to 10 MB),
 * an optional custom header title, and a custom legal footer.
 *
 * Empty values fall back to the built-in KR Enterprise defaults on the
 * PDF renderer.
 */
export default function PackingSlipTemplateDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ header_title: '', legal_footer: '' });
  const [hasLogo, setHasLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // ── Live preview state ────────────────────────────────────────
  // `previewUrl` is a blob: URL we own and revoke on refresh/unmount so
  // the browser doesn't leak PDFs. `previewLoading` powers the subtle
  // shimmer while a re-render is in flight.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewDebounceRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await suppliesAPI.getTemplate();
      setForm({
        header_title: data.header_title || '',
        legal_footer: data.legal_footer || '',
      });
      setHasLogo(!!data.logo_storage_key);
      // Fetch logo blob for preview
      if (data.logo_storage_key) {
        try {
          const res = await suppliesAPI.templateLogoBlob();
          setLogoUrl(URL.createObjectURL(res.data));
        } catch {
          setLogoUrl(null);
        }
      } else {
        setLogoUrl(null);
      }
    } catch (e) {
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    return () => {
      if (logoUrl) URL.revokeObjectURL(logoUrl);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced live preview: refresh 500ms after the last keystroke or
  // any change to `hasLogo` (upload/delete). We POST the current form
  // state so the server merges it with the saved template + logo.
  const refreshPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await suppliesAPI.previewTemplate({
        header_title: form.header_title,
        legal_footer: form.legal_footer,
      });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) {
      // Silent — the pane just keeps showing the previous frame.
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!open || loading) return;
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(refreshPreview, 500);
    return () => clearTimeout(previewDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, form.header_title, form.legal_footer, hasLogo]);

  const saveText = async () => {
    setBusy(true);
    try {
      await suppliesAPI.updateTemplate({
        header_title: form.header_title.trim(),
        legal_footer: form.legal_footer.trim(),
      });
      toast.success('Template saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const pickLogo = () => fileRef.current?.click();

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be ≤ 10 MB');
      return;
    }
    setBusy(true);
    try {
      await suppliesAPI.uploadTemplateLogo(file);
      toast.success('Logo uploaded');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm('Remove the packing-slip logo?')) return;
    setBusy(true);
    try {
      await suppliesAPI.deleteTemplateLogo();
      toast.success('Logo removed');
      await load();
    } catch {
      toast.error('Remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle>Packing Slip Template</DialogTitle>
          <DialogDescription>
            Brand the printed Packing Slip PDF with your logo, a custom header
            and a legal footer. Live preview updates as you type. Blank values
            keep the KR Enterprise defaults.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-500">
            <Loader2 className="animate-spin mx-auto mb-2" /> Loading...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr),minmax(0,1.1fr)] gap-6 flex-1 overflow-y-auto pr-1">
            {/* ── Left column: form ─────────────────────────────── */}
            <div className="space-y-5">
              <div>
                <Label className="text-sm font-medium mb-1 block">Logo</Label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
                    {logoUrl ? (
                      <img src={logoUrl} alt="Packing slip logo" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={22} className="text-slate-300" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleLogoFile}
                      data-testid="template-logo-input"
                    />
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm"
                              onClick={pickLogo} disabled={busy}
                              data-testid="template-logo-upload">
                        <Camera size={14} className="mr-1" />
                        {hasLogo ? 'Replace' : 'Upload'}
                      </Button>
                      {hasLogo && (
                        <Button type="button" variant="ghost" size="sm"
                                onClick={removeLogo} disabled={busy}
                                className="text-rose-600">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 max-w-[240px]">
                      JPG / PNG / WebP up to 10 MB. Auto-cropped to a square.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Header Title</Label>
                <Input
                  value={form.header_title}
                  onChange={(e) => setForm({ ...form, header_title: e.target.value })}
                  placeholder="KR ENTERPRISE GROUP"
                  maxLength={120}
                  data-testid="template-header-input"
                />
                <p className="text-xs text-slate-500">Shown at the top of every packing slip.</p>
              </div>

              <div className="space-y-1.5">
                <Label>Legal Footer</Label>
                <Textarea
                  value={form.legal_footer}
                  onChange={(e) => setForm({ ...form, legal_footer: e.target.value })}
                  rows={5}
                  placeholder="By signing above, the agent acknowledges receipt of the equipment listed and agrees to return all items in the same condition by the Return Date."
                  maxLength={2000}
                  data-testid="template-footer-input"
                />
                <p className="text-xs text-slate-500">
                  Printed under the signature block. Use plain text.
                </p>
              </div>
            </div>

            {/* ── Right column: live PDF preview ────────────────── */}
            <div className="flex flex-col min-h-0" data-testid="template-preview-pane">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <Eye size={14} /> Live preview
                </div>
                {previewLoading && (
                  <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <Loader2 size={12} className="animate-spin" /> Rendering...
                  </span>
                )}
              </div>
              <div className="border border-slate-200 rounded-md bg-slate-50 overflow-hidden flex-1 min-h-[420px] relative">
                {previewUrl ? (
                  <>
                    <iframe
                      key={previewUrl}
                      src={`${previewUrl}#toolbar=0&navpanes=0&view=FitH`}
                      title="Packing slip preview"
                      className="w-full h-full"
                      data-testid="template-preview-iframe"
                    />
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="absolute bottom-2 right-2 text-xs bg-white/95 border border-slate-200 rounded px-2 py-1 shadow-sm text-slate-700 hover:text-slate-900"
                      data-testid="template-preview-open"
                    >
                      Open in new tab
                    </a>
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    <Loader2 className="animate-spin mr-2" size={16} /> Generating preview...
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Preview uses sample loan data. Real events pull agent + inventory info at check-out.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={saveText} disabled={busy || loading} data-testid="template-save">
            {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <Palette size={14} className="mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
